import type { FC } from 'react';
import React from 'react';
import { css, keyframes } from '@compiled/react';
import { defineMessages, useIntl } from 'react-intl-next';

import { Inline, Text } from '@atlaskit/primitives/compiled';
import { cssMap } from '@atlaskit/css';
import { token } from '@atlaskit/tokens';
import Tooltip from '@atlaskit/tooltip';

import { ConfluenceLabsIcon } from '../assets/ConfluenceLabsIcon';

const i18n = defineMessages({
	tooltip: {
		id: 'ai-common.confluence-labs-lozenge.tooltip',
		defaultMessage: '{confluenceLabsBrandName} features are experimental and still in development.',
		description:
			'Tooltip message when the user hovers over the Confluence Labs lozenge letting them know that the features are experimental',
	},
});

const lozengeStyles = cssMap({
	lozenge: {
		paddingInline: token('space.050'),
		borderRadius: token('border.radius'),
		display: 'inline-block',
	},
});

const gradientWaveKeyframes = keyframes({
	'0%': {
		backgroundPosition: '0% 50%',
		backgroundSize: '200% 100%',
	},
	'25%': {
		backgroundPosition: '50% 30%',
		backgroundSize: '250% 100%',
	},
	'50%': {
		backgroundPosition: '100% 50%',
		backgroundSize: '200% 100%',
	},
	'75%': {
		backgroundPosition: '50% 70%',
		backgroundSize: '250% 100%',
	},
	'100%': {
		backgroundPosition: '0% 50%',
		backgroundSize: '200% 100%',
	},
});

const lozengeAnimatedBackground = css({
	background: `linear-gradient(270deg, ${token('color.background.accent.yellow.subtlest.pressed')} 0%, ${token('color.background.accent.magenta.subtlest.pressed')} 100%)`,
	animation: `${gradientWaveKeyframes} 4s ease-in-out infinite`,
});

export const ConfluenceLabsLozenge: FC = () => {
	const { formatMessage } = useIntl();

	// We don't want to i18n the term 'Confluence Labs' because it is the branding name
	// and should be the same across all languages
	return (
		<Tooltip content={formatMessage(i18n.tooltip, { confluenceLabsBrandName: 'Confluence Labs' })}>
			<div
				css={[lozengeStyles.lozenge, lozengeAnimatedBackground]}
				data-testid="confluence-labs-lozenge"
			>
				<Inline grow="hug" space="space.025" alignBlock="center">
					<ConfluenceLabsIcon />
					<Text size="small" weight="semibold">
						Labs
					</Text>
				</Inline>
			</div>
		</Tooltip>
	);
};
